/**
 * SignoffModal — send a draft for review, approve it, ask for changes, or sign it.
 *
 * The rules are the server's (who may do what, when); this offers the steps that
 * make sense for the viewer and shows what the server says if it refuses one.
 * The trail underneath is the draft's own history, so a reader can see who
 * checked and signed, and when an edit voided it.
 */

import { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import Button from '../common/Button';
import Chip from '../common/Chip';
import { draftsAPI } from '../../services/api';
import { useUI } from '../../context/UIContext';
import { actionsFor, historyLines, needsChecker, statusView } from '../../utils/signoffView';

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export default function SignoffModal({ open, draft, isProfessional, onClose, onChanged }) {
  const { showToast } = useUI();
  const [signoff, setSignoff] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false); // the "ask for changes" note box is open

  // Load the full draft once: the list leaves the history out to stay light.
  useEffect(() => {
    if (!open || !draft?._id) return;
    setNote('');
    setAsking(false);
    setSignoff(draft.signoff || null);
    let live = true;
    draftsAPI.getById(draft._id).then((res) => {
      if (live && res?.success && res.data?.signoff) setSignoff(res.data.signoff);
    }).catch(() => { /* the list's copy is enough to show */ });
    return () => { live = false; };
  }, [open, draft]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open || !draft) return null;

  const view = statusView(signoff);
  const actions = actionsFor({ signoff, documentType: draft.documentType, isProfessional, canEdit: draft.canEdit !== false });
  const lines = historyLines(signoff);

  const run = async (a) => {
    if (a.needsNote && !asking) { setAsking(true); return; }
    if (a.needsNote && !note.trim()) { showToast('Say what needs to change', 'error'); return; }
    setBusy(true);
    try {
      const res = await draftsAPI.signoff(draft._id, { action: a.action, note: a.needsNote ? note : undefined });
      if (res?.success) {
        const full = await draftsAPI.getById(draft._id).catch(() => null);
        const next = full?.data?.signoff || { ...(signoff || {}), status: res.data?.signoff?.status };
        setSignoff(next);
        setAsking(false);
        setNote('');
        onChanged?.(draft._id, next);
        showToast(res.message || 'Recorded.', 'success');
      } else {
        showToast(res?.message || 'That did not go through', 'error');
      }
    } catch (err) {
      // The server's reason is plain language ("someone else has to approve it").
      showToast(err.message || 'That did not go through', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(8,17,31,0.55)' }}>
      <div className="bg-white rounded-[12px] w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ border: '1px solid #ddd6c6' }}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-[18px] font-bold text-[#08111f]">Review and sign</h2>
              <p className="text-[13px] text-[#7a7060] mt-0.5">{draft.documentTitle}</p>
            </div>
            <button aria-label="Close" className="w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] hover:bg-[#9a564a]/10 hover:text-[#9a564a]" onClick={onClose} disabled={busy}>
              <FiX size={17} />
            </button>
          </div>

          <div className="flex items-center gap-2 mb-1">
            <Chip bg={view.bg} color={view.color}>{view.label}</Chip>
            {signoff?.selfApproved && <Chip bg="#f3ebd7" color="#8a6a1f" title="Nobody else looked at this draft before it was approved">Self-approved</Chip>}
          </div>
          <p className="text-[13px] text-[#4b4638] mb-1">{view.hint}</p>
          <p className="text-[12px] text-[#7a7060] mb-4">
            {needsChecker(draft.documentType)
              ? 'This kind of document must be checked and approved before a professional signs it.'
              : 'A professional may sign this kind of document without a separate review.'}
          </p>

          {signoff?.status === 'changes_requested' && signoff.note && (
            <div className="rounded-[8px] p-3 mb-4 text-[13px]" style={{ background: '#f4e4e0', color: '#6e3a30' }}>
              <strong>Reviewer&rsquo;s note:</strong> {signoff.note}
            </div>
          )}

          {asking && (
            <textarea
              className="w-full rounded-[8px] p-3 mb-3 text-[13px]"
              style={{ border: '1px solid #ddd6c6' }}
              rows={3}
              maxLength={1000}
              placeholder="What needs to change?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
            />
          )}

          <div className="flex flex-wrap gap-2 mb-5">
            {actions.length === 0 && <span className="text-[13px] text-[#7a7060]">Nothing for you to do on this document right now.</span>}
            {actions.map((a) => (
              <Button key={`${a.action}-${a.label}`} size="sm" variant={a.primary ? 'primary' : 'outline'} disabled={busy} onClick={() => run(a)}>
                {a.needsNote && asking ? 'Send to maker' : a.label}
              </Button>
            ))}
          </div>

          {lines.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-[#7a7060] mb-2">History</h3>
              <ul className="space-y-1.5">
                {lines.map((l, i) => (
                  <li key={`${l.at}-${i}`} className="text-[13px] text-[#08111f]">
                    {l.text} <span className="text-[#7a7060]">· {fmt(l.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
